/**
 * Property Relationship Service
 * 
 * Service for extracting and managing property relationships.
 * Works with current backend API and forward-compatible with upcoming enhancements.
 */

import { apiClient } from '../apiClient';
import {
  PropertyGraph,
  PropertyGraphNode,
  PropertyGraphEdge,
  PropertyRelationship,
  PropertyRelationships,
  RelationshipType,
  PropertyDependency,
  PropertyDependent,
  PropertyGraphResponse
} from '../../types/propertyRelationships';

interface PrimeArtifactProperty {
  id: string;
  prime_artifact_id: string;
  property_key: string;
  property_type: string;
  description?: string;
  is_required: boolean;
  input_source?: 'user_input' | 'system_generated' | 'referenced_artifact' | 'transformed_reference';
  default_value?: string;
  referenced_artifacts?: Array<{
    id: string;
    transformation_logic?: any;
    reference_type?: 'uuid' | 'transformed';
    source_property_key?: string;
    artifact_id?: string;
  }>;
  state_mappings?: Array<{
    state_name: string;
    mapping_type: 'created' | 'updated';
  }>;
  // Forward-compatible fields
  relationship_type?: string;
  relationship_metadata?: Record<string, any>;
}

export class PropertyRelationshipService {
  /**
   * Get property graph for an artifact.
   * Tries new endpoint first, falls back to building from property list.
   */
  static async getPropertyGraph(artifactId: string): Promise<PropertyGraph> {
    try {
      // Try new graph endpoint (forward-compatible)
      const response = await apiClient.get<PropertyGraphResponse>(
        `/prime-artifacts/${artifactId}/properties/graph`
      );
      return this.normalizeGraphResponse(response.data);
    } catch (error: any) {
      // Fallback: build graph from current property list endpoint
      // Handle 404, 500, and network errors by falling back to building from properties
      if (error.response?.status === 404 || error.response?.status === 500 || error.code === 'ERR_NETWORK') {
        console.warn(`[PropertyRelationshipService] Graph endpoint returned ${error.response?.status || 'error'}, falling back to building graph from properties`);
        return this.buildGraphFromProperties(artifactId);
      }
      throw error;
    }
  }

  /**
   * Get relationships for a specific property.
   */
  static async getPropertyRelationships(
    artifactId: string,
    propertyId: string
  ): Promise<PropertyRelationships> {
    try {
      // Try new endpoint
      const response = await apiClient.get<PropertyRelationships>(
        `/prime-artifacts/${artifactId}/properties/${propertyId}/relationships`
      );
      return response.data;
    } catch (error: any) {
      // Fallback: build from property data
      if (error.response?.status === 404) {
        const properties = await this.getProperties(artifactId);
        const property = properties.find(p => p.id === propertyId);
        if (!property) {
          throw new Error(`Property ${propertyId} not found`);
        }
        return this.buildRelationshipsFromProperty(property, properties);
      }
      throw error;
    }
  }

  /**
   * Extract relationships from transformation logic.
   */
  static extractRelationshipsFromTransformationLogic(
    transformationLogic: any,
    sourcePropertyId: string,
    sourcePropertyKey: string
  ): PropertyRelationship[] {
    if (!transformationLogic || typeof transformationLogic !== 'object') {
      return [];
    }

    const relationships: PropertyRelationship[] = [];
    const calcType = transformationLogic.type;
    const sourceProperties = transformationLogic.source_properties || {};

    // Extract dependencies from source_properties
    for (const [key, spec] of Object.entries(sourceProperties)) {
      if (typeof spec === 'object' && spec !== null) {
        const source = (spec as any).source;
        const propertyKey = (spec as any).property_key;
        const variableKey = (spec as any).variable_key;

        if (source === 'same_artifact' && propertyKey) {
          relationships.push({
            id: `${sourcePropertyId}-depends-${propertyKey}`,
            sourcePropertyId,
            sourcePropertyKey,
            targetPropertyKey: propertyKey,
            relationshipType: this.getRelationshipTypeFromCalculationType(calcType),
            calculationType: calcType,
            formula: this.buildFormula(calcType, sourcePropertyKey, propertyKey, transformationLogic)
          });
        } else if (source === 'global_variable' && variableKey) {
          relationships.push({
            id: `${sourcePropertyId}-global-${variableKey}`,
            sourcePropertyId,
            sourcePropertyKey,
            globalVariableKey: variableKey,
            relationshipType: RelationshipType.GLOBAL_REF,
            isGlobalVariable: true
          });
        }
      }
    }

    return relationships;
  }

  /**
   * Determine relationship type from property definition.
   */
  static getRelationshipType(property: PrimeArtifactProperty): RelationshipType {
    // Check if backend provides relationship_type (forward-compatible)
    if (property.relationship_type) {
      return this.mapRelationshipType(property.relationship_type);
    }

    // Infer from input_source and transformation_logic
    if (property.input_source === 'transformed_reference') {
      const ref = property.referenced_artifacts?.[0];
      if (ref?.transformation_logic) {
        const calcType = ref.transformation_logic.type;
        if (calcType === 'divide') {
          return RelationshipType.RATIO;
        } else if (calcType === 'aggregation' || calcType === 'aggregate_from_items') {
          return RelationshipType.AGGREGATION;
        } else if (calcType === 'conditional') {
          return RelationshipType.CONDITION;
        } else if (calcType === 'switch') {
          return RelationshipType.SWITCH;
        } else if (['add', 'subtract', 'multiply', 'divide'].includes(calcType)) {
          return RelationshipType.CALCULATION;
        }
      }
      return RelationshipType.CALCULATION;
    } else if (property.input_source === 'user_input') {
      return RelationshipType.INPUT;
    } else if (property.default_value) {
      return RelationshipType.DEFAULT;
    } else if (property.input_source === 'referenced_artifact') {
      return RelationshipType.REFERENCED_ARTIFACT;
    }

    return RelationshipType.UNKNOWN;
  }

  /**
   * Build graph from property list (fallback method).
   */
  private static async buildGraphFromProperties(artifactId: string): Promise<PropertyGraph> {
    const properties = await this.getProperties(artifactId);
    const nodes: PropertyGraphNode[] = [];
    const edges: PropertyGraphEdge[] = [];
    const propertyMap = new Map<string, PrimeArtifactProperty>();

    // Create nodes
    for (const property of properties) {
      propertyMap.set(property.id, property);
      const relationshipType = this.getRelationshipType(property);
      
      nodes.push({
        id: property.id,
        propertyId: property.id,
        propertyKey: property.property_key,
        propertyType: property.property_type,
        relationshipType,
        description: property.description,
        inputSource: property.input_source,
        isRequired: property.is_required,
        defaultValue: property.default_value,
        relationshipMetadata: property.relationship_metadata
      });

      // Extract relationships from transformation logic
      if (property.referenced_artifacts) {
        for (const ref of property.referenced_artifacts) {
          if (ref.transformation_logic) {
            const relationships = this.extractRelationshipsFromTransformationLogic(
              ref.transformation_logic,
              property.id,
              property.property_key
            );

            for (const rel of relationships) {
              if (rel.targetPropertyKey) {
                // Find target property
                const targetProperty = Array.from(propertyMap.values()).find(
                  p => p.property_key === rel.targetPropertyKey
                );

                if (targetProperty) {
                  edges.push({
                    id: `${property.id}-${targetProperty.id}`,
                    source: property.id,
                    target: targetProperty.id,
                    relationshipType: rel.relationshipType,
                    calculationType: rel.calculationType,
                    formula: rel.formula,
                    label: rel.formula || rel.calculationType
                  });
                }
              } else if (rel.isGlobalVariable && rel.globalVariableKey) {
                // Global variable reference
                edges.push({
                  id: `${property.id}-global-${rel.globalVariableKey}`,
                  source: property.id,
                  target: `global-${rel.globalVariableKey}`,
                  relationshipType: RelationshipType.GLOBAL_REF,
                  isGlobalVariable: true,
                  label: `Global: ${rel.globalVariableKey}`
                });
              }
            }
          }
        }
      }
    }

    // Calculate relationship summary
    const summary = this.calculateRelationshipSummary(nodes);

    return {
      nodes,
      edges,
      relationshipSummary: summary
    };
  }

  /**
   * Build relationships object from property.
   */
  private static buildRelationshipsFromProperty(
    property: PrimeArtifactProperty,
    allProperties: PrimeArtifactProperty[]
  ): PropertyRelationships {
    const dependencies: PropertyDependency[] = [];
    const dependents: PropertyDependent[] = [];
    const relationshipType = this.getRelationshipType(property);

    // Find dependencies
    if (property.referenced_artifacts) {
      for (const ref of property.referenced_artifacts) {
        if (ref.transformation_logic) {
          const relationships = this.extractRelationshipsFromTransformationLogic(
            ref.transformation_logic,
            property.id,
            property.property_key
          );

          for (const rel of relationships) {
            if (rel.targetPropertyKey) {
              const targetProperty = allProperties.find(
                p => p.property_key === rel.targetPropertyKey
              );
              if (targetProperty) {
                dependencies.push({
                  propertyId: targetProperty.id,
                  propertyKey: targetProperty.property_key,
                  relationshipType: rel.relationshipType,
                  calculationType: rel.calculationType,
                  formula: rel.formula
                });
              }
            }
          }
        }
      }
    }

    // Find dependents (properties that depend on this one)
    for (const otherProperty of allProperties) {
      if (otherProperty.id === property.id) continue;

      if (otherProperty.referenced_artifacts) {
        for (const ref of otherProperty.referenced_artifacts) {
          if (ref.transformation_logic) {
            const relationships = this.extractRelationshipsFromTransformationLogic(
              ref.transformation_logic,
              otherProperty.id,
              otherProperty.property_key
            );

            for (const rel of relationships) {
              if (rel.targetPropertyKey === property.property_key) {
                dependents.push({
                  propertyId: otherProperty.id,
                  propertyKey: otherProperty.property_key,
                  relationshipType: rel.relationshipType,
                  calculationType: rel.calculationType,
                  formula: rel.formula,
                  howUsed: rel.formula || `${rel.calculationType} calculation`
                });
              }
            }
          }
        }
      }
    }

    return {
      propertyId: property.id,
      propertyKey: property.property_key,
      dependencies,
      dependents,
      relationshipType
    };
  }

  /**
   * Get properties for an artifact.
   */
  private static async getProperties(artifactId: string): Promise<PrimeArtifactProperty[]> {
    const response = await apiClient.get<PrimeArtifactProperty[]>(
      `/prime-artifacts/${artifactId}/properties`
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Normalize graph response from backend.
   */
  private static normalizeGraphResponse(data: PropertyGraphResponse): PropertyGraph {
    const summary = data.relationship_summary;
    return {
      nodes: data.nodes,
      edges: data.edges,
      relationshipSummary: summary ? {
        calculations: summary.calculations || 0,
        ratios: summary.ratios || 0,
        aggregations: summary.aggregations || 0,
        conditions: summary.conditions || 0,
        switches: summary.switches || 0,
        globalRefs: summary.global_refs || 0,
        inputs: summary.inputs || 0,
        defaults: summary.defaults || 0
      } : undefined
    };
  }

  /**
   * Get relationship type from calculation type.
   */
  private static getRelationshipTypeFromCalculationType(calcType: string): RelationshipType {
    if (calcType === 'divide') {
      return RelationshipType.RATIO;
    } else if (calcType === 'aggregation' || calcType === 'aggregate_from_items') {
      return RelationshipType.AGGREGATION;
    } else if (calcType === 'conditional') {
      return RelationshipType.CONDITION;
    } else if (calcType === 'switch') {
      return RelationshipType.SWITCH;
    } else if (['add', 'subtract', 'multiply', 'divide'].includes(calcType)) {
      return RelationshipType.CALCULATION;
    }
    return RelationshipType.UNKNOWN;
  }

  /**
   * Map string relationship type to enum.
   */
  private static mapRelationshipType(type: string): RelationshipType {
    const mapping: Record<string, RelationshipType> = {
      'calculation': RelationshipType.CALCULATION,
      'ratio': RelationshipType.RATIO,
      'aggregation': RelationshipType.AGGREGATION,
      'condition': RelationshipType.CONDITION,
      'switch': RelationshipType.SWITCH,
      'input': RelationshipType.INPUT,
      'default': RelationshipType.DEFAULT,
      'global_ref': RelationshipType.GLOBAL_REF,
      'referenced_artifact': RelationshipType.REFERENCED_ARTIFACT
    };
    return mapping[type] || RelationshipType.UNKNOWN;
  }

  /**
   * Build human-readable formula.
   */
  private static buildFormula(
    calcType: string,
    resultKey: string,
    sourceKey: string,
    logic: any
  ): string {
    switch (calcType) {
      case 'add':
        return `${resultKey} = ${sourceKey} + ...`;
      case 'subtract': {
        const sources = logic.source_properties || {};
        const op1 = sources.operand1?.property_key || '?';
        const op2 = sources.operand2?.property_key || '?';
        return `${resultKey} = ${op1} - ${op2}`;
      }
      case 'multiply':
        return `${resultKey} = ${sourceKey} * ...`;
      case 'divide': {
        const sources = logic.source_properties || {};
        const divOp1 = sources.operand1?.property_key || '?';
        const divOp2 = sources.operand2?.property_key || '?';
        return `${resultKey} = ${divOp1} / ${divOp2}`;
      }
      case 'aggregation':
        return `${resultKey} = sum(${sourceKey})`;
      case 'conditional':
        return `${resultKey} = if(${sourceKey} ...)`;
      default:
        return `${resultKey} = f(${sourceKey})`;
    }
  }

  /**
   * Calculate relationship summary.
   */
  private static calculateRelationshipSummary(nodes: PropertyGraphNode[]): PropertyGraph['relationshipSummary'] {
    const summary = {
      calculations: 0,
      ratios: 0,
      aggregations: 0,
      conditions: 0,
      switches: 0,
      globalRefs: 0,
      inputs: 0,
      defaults: 0
    };

    for (const node of nodes) {
      switch (node.relationshipType) {
        case RelationshipType.CALCULATION:
          summary.calculations++;
          break;
        case RelationshipType.RATIO:
          summary.ratios++;
          break;
        case RelationshipType.AGGREGATION:
          summary.aggregations++;
          break;
        case RelationshipType.CONDITION:
          summary.conditions++;
          break;
        case RelationshipType.SWITCH:
          summary.switches++;
          break;
        case RelationshipType.GLOBAL_REF:
          summary.globalRefs++;
          break;
        case RelationshipType.INPUT:
          summary.inputs++;
          break;
        case RelationshipType.DEFAULT:
          summary.defaults++;
          break;
      }
    }

    return summary;
  }
}

